import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';

/**
 * BONUS_WEDGE deployer marker — pinned on the dial at the wedge
 * that the deployer's BONUS_WEDGE replaces. Producer brief
 * 2026-05-15: every player sees the bonus wedge appear in real-
 * time during STAKE with the deployer's avatar attached so the
 * attribution is unmistakable.
 *
 * <p>Visual: the deployer's avatar fills a disc with a coloured
 * ring (cyan for self, orange for rivals), plus a small gold
 * "×3" coin badge in the lower-right corner. The marker sits
 * inside the rotating disc Container so it travels with the
 * wedge through the spin; counter-rotation on the host keeps it
 * upright in viewport frame.
 */

const FONT_FAMILY = '"Fredoka", system-ui, -apple-system, sans-serif';
const WEDGE_ARC_DEG = 360 / 24;

export interface WheelResolveBonusMarkerOptions {
  /** Diameter of the avatar disc in canvas pixels. */
  diameter: number;
  /** Radius (centre → marker centre) — pin the marker on the
   *  wedge's mid-arc point. Defaults to ~38% of the disc radius. */
  wedgeLabelRadius: number;
}

export interface WheelResolveBonusMarkerState {
  wedgeIndex: number;
  avatarTexture: Texture | null;
  displayName: string;
  isSelf: boolean;
}

export class WheelResolveBonusMarker extends Container {
  private readonly avatarHost: Container;
  private readonly avatarMask: Graphics;
  private readonly avatarRing: Graphics;
  private avatarSprite: Sprite | null = null;
  private fallbackText: Text | null = null;
  private readonly coinBadge: Container;
  private readonly opts: WheelResolveBonusMarkerOptions;

  constructor(options: WheelResolveBonusMarkerOptions) {
    super();
    this.opts = options;
    const d = options.diameter;

    this.avatarHost = new Container();
    this.addChild(this.avatarHost);
    this.avatarMask = new Graphics();
    this.avatarMask.circle(0, 0, d / 2).fill({ color: 0xffffff });
    this.avatarHost.addChild(this.avatarMask);
    this.avatarHost.mask = this.avatarMask;

    this.avatarRing = new Graphics();
    this.addChild(this.avatarRing);

    // Gold ×3 coin badge in the lower-right corner.
    this.coinBadge = new Container();
    const badgeR = Math.round(d * 0.28);
    const badgeBg = new Graphics();
    badgeBg
      .circle(0, 0, badgeR)
      .fill({ color: 0xffc940, alpha: 1 })
      .circle(0, 0, badgeR)
      .stroke({ color: 0xffffff, alpha: 1, width: 2 });
    this.coinBadge.addChild(badgeBg);
    const badgeText = new Text({
      text: '×3',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: Math.round(badgeR * 0.9),
        fontWeight: '900',
        fill: 0x3a1e04,
      },
    });
    badgeText.anchor.set(0.5);
    this.coinBadge.addChild(badgeText);
    this.coinBadge.x = d * 0.32;
    this.coinBadge.y = d * 0.32;
    this.addChild(this.coinBadge);
  }

  setState(state: WheelResolveBonusMarkerState): void {
    // Position on the wedge centre.
    const midAngleDeg = (state.wedgeIndex + 0.5) * WEDGE_ARC_DEG;
    const angleRad = ((midAngleDeg - 90) * Math.PI) / 180;
    this.x = Math.cos(angleRad) * this.opts.wedgeLabelRadius;
    this.y = Math.sin(angleRad) * this.opts.wedgeLabelRadius;
    // Producer feedback 2026-05-17 — orient the marker so its
    // local UP points OUTWARD from the wheel hub (the wedge's
    // outward radial direction). Combined with the marker being
    // parented inside the disc's rotatingOverlay, this makes the
    // marker behave like a physical sticker glued to the wedge —
    // it rotates 1:1 with the spinning wheel.
    this.angle = midAngleDeg;

    // Refresh avatar contents.
    if (this.avatarSprite) {
      this.avatarSprite.destroy();
      this.avatarSprite = null;
    }
    if (this.fallbackText) {
      this.fallbackText.destroy();
      this.fallbackText = null;
    }
    if (state.avatarTexture) {
      const sprite = new Sprite(state.avatarTexture);
      sprite.anchor.set(0.5);
      sprite.width = this.opts.diameter;
      sprite.height = this.opts.diameter;
      this.avatarHost.addChild(sprite);
      this.avatarSprite = sprite;
    } else {
      const initial = (state.displayName || '?').slice(0, 1).toUpperCase();
      const text = new Text({
        text: initial,
        style: {
          fontFamily: FONT_FAMILY,
          fontSize: Math.round(this.opts.diameter * 0.5),
          fontWeight: '900',
          fill: 0xffe9a6,
        },
      });
      text.anchor.set(0.5);
      this.avatarHost.addChild(text);
      this.fallbackText = text;
    }

    const accent = state.isSelf ? 0x6be0ff : 0xff8e5a;
    this.avatarRing.clear();
    this.avatarRing
      .circle(0, 0, this.opts.diameter / 2 + 1)
      .stroke({ color: accent, alpha: 1, width: 2 })
      .circle(0, 0, this.opts.diameter / 2 + 4)
      .stroke({ color: accent, alpha: 0.45, width: 4 });
  }

  /** Producer feedback 2026-05-17 — markers stay locked to their
   *  wedge like a sticker on a physical wheel, so we no longer
   *  counter-rotate. Method kept (no-op) for API compatibility in
   *  case callers reference it. */
  setCounterRotation(_deg: number): void {
    // intentionally a no-op
  }

  destroyDisplay(): void {
    this.removeChildren();
  }
}
